import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../../../shared/context/AuthContext'

interface CompletionFormData {
  password: string
  confirmPassword: string
}

interface InvitationData {
  companyName: string
  contactFirstName: string
  contactLastName: string
  email: string
}

const AccountCompletionForm: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { checkAuthStatus } = useAuth()
  
  const [invitationData, setInvitationData] = useState<InvitationData | null>(null)
  const [formData, setFormData] = useState<CompletionFormData>({
    password: '',
    confirmPassword: ''
  })
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [tokenError, setTokenError] = useState<string | null>(null)

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setTokenError('Invalid invitation link')
        setIsLoading(false)
        return
      }

      try {
        const response = await fetch(`/api/invitations/verify/${token}`, {
          credentials: 'include'
        })

        if (response.ok) {
          const data = await response.json()
          setInvitationData(data.data)
        } else {
          const errorData = await response.json()
          setTokenError(errorData.message || 'Invalid or expired invitation link')
        }
      } catch (err) {
        console.error('Token verification error:', err)
        setTokenError('Unable to verify invitation. Please check your internet connection and try again.')
      } finally {
        setIsLoading(false)
      }
    }

    verifyToken()
  }, [token])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear error when user starts typing
    if (error) setError(null)
  }

  const validateForm = (): string | null => {
    if (!formData.password) {
      return 'Password is required'
    }
    if (formData.password.length < 8) {
      return 'Password must be at least 8 characters long'
    }
    if (formData.password !== formData.confirmPassword) {
      return 'Passwords do not match'
    }
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    const validationError = validateForm()
    if (validationError) {
      setError(validationError)
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const response = await fetch(`/api/invitations/complete/${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          name: `${invitationData?.contactFirstName} ${invitationData?.contactLastName}`.trim(),
          password: formData.password
        })
      })

      const data = await response.json()

      if (response.ok) {
        // Account created successfully, user is automatically logged in
        // Update AuthContext with new user data before navigating
        console.log('🔄 Account creation successful, updating auth context...')
        await checkAuthStatus()
        
        // Navigate to dashboard (DashboardRedirect will handle role-based routing)
        console.log('✅ Auth context updated, navigating to dashboard...')
        navigate('/dashboard')
      } else {
        setError(data.message || 'An error occurred while creating your account')
      }
    } catch (err) {
      console.error('Account completion error:', err)
      setError('Unable to create your account. Please check your internet connection and try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const getPasswordStrength = (password: string): { strength: string; color: string; width: string } => {
    if (password.length === 0) return { strength: '', color: '', width: '0%' }
    if (password.length < 6) return { strength: 'Weak', color: 'danger', width: '25%' }
    if (password.length < 8) return { strength: 'Fair', color: 'warning', width: '50%' }
    if (password.length < 12) return { strength: 'Good', color: 'info', width: '75%' }
    return { strength: 'Strong', color: 'success', width: '100%' }
  }

  const passwordStrength = getPasswordStrength(formData.password)

  if (isLoading) {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="card shadow">
              <div className="card-body text-center py-5">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <h5>Verifying invitation...</h5>
                <p className="text-muted">Please wait while we verify your invitation link.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (tokenError) {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="card shadow">
              <div className="card-body text-center py-5">
                <div className="mb-4">
                  <i className="bi bi-exclamation-triangle-fill text-danger" style={{ fontSize: '3rem' }}></i>
                </div>
                <h3 className="text-danger mb-3">Invalid Invitation</h3>
                <p className="lead mb-4">{tokenError}</p>
                <div className="alert alert-info">
                  <h6 className="alert-heading">What can you do?</h6>
                  <ul className="text-start mb-0">
                    <li>Check that you used the complete link from your email</li>
                    <li>Contact our support team if you need a new invitation</li>
                    <li>Try submitting a new sign-up request</li>
                  </ul>
                </div>
                <div className="d-grid gap-2">
                  <button 
                    className="btn btn-primary"
                    onClick={() => navigate('/signup')}
                  >
                    Submit New Request
                  </button>
                  <button 
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/')}
                  >
                    Return to Home
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container mt-5">
      <div className="row justify-content-center">
        <div className="col-md-8 col-lg-6">
          <div className="card shadow">
            <div className="card-header bg-success text-white">
              <h3 className="card-title mb-0">
                <i className="bi bi-key me-2"></i>
                Create Your Password
              </h3>
            </div>
            <div className="card-body">
              {invitationData && (
                <div className="alert alert-success mb-4">
                  <h6 className="alert-heading">Welcome {invitationData.contactFirstName}!</h6>
                  <p className="mb-0">
                    Your ISO Portal access has been approved. Just create a password to complete your account setup.
                  </p>
                </div>
              )}

              {error && (
                <div className="alert alert-danger" role="alert">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {error}
                </div>
              )}



              <form onSubmit={handleSubmit}>

                <div className="mb-3">
                  <label htmlFor="password" className="form-label">
                    Password <span className="text-danger">*</span>
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    placeholder="Create a secure password"
                  />
                  {formData.password && (
                    <div className="mt-2">
                      <div className="d-flex justify-content-between">
                        <small className="form-text">Password strength:</small>
                        <small className={`text-${passwordStrength.color}`}>
                          {passwordStrength.strength}
                        </small>
                      </div>
                      <div className="progress" style={{ height: '5px' }}>
                        <div 
                          className={`progress-bar bg-${passwordStrength.color}`}
                          style={{ width: passwordStrength.width }}
                        ></div>
                      </div>
                    </div>
                  )}
                  <div className="form-text">Must be at least 8 characters long</div>
                </div>

                <div className="mb-4">
                  <label htmlFor="confirmPassword" className="form-label">
                    Confirm Password <span className="text-danger">*</span>
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    placeholder="Confirm your password"
                  />
                  {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                    <div className="form-text text-danger">
                      <i className="bi bi-exclamation-triangle me-1"></i>
                      Passwords do not match
                    </div>
                  )}
                </div>

                <div className="d-grid">
                  <button 
                    type="submit" 
                    className="btn btn-success btn-lg"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Creating Account...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-shield-lock me-2"></i>
                        Create Account & Login
                      </>
                    )}
                  </button>
                </div>
              </form>

              <div className="mt-4 pt-3 border-top">
                <p className="text-muted small mb-0">
                  <i className="bi bi-shield-check me-1"></i>
                  Your password is encrypted and secure. By creating your account, you agree to our terms of service.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AccountCompletionForm